import React, { useEffect, useRef } from 'react';
import { Star, Quote } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import SectionHeader from './SectionHeader';

const testimonials = [
  {
    name: 'Sarah Mitchell',
    role: 'UI/UX Designer',
    image: 'https://images.unsplash.com/photo-1581065178047-8ee15951ede6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NjMzNzUyM3ww&ixlib=rb-4.1.0&q=85',
    rating: 5,
    text: 'Kaamhub transformed my freelance career! I found quality clients within days and the platform makes communication seamless. Best decision I made this year.',
    gradient: 'from-blue-500 to-cyan-400'
  },
  {
    name: 'Marcus Johnson',
    role: 'Full Stack Developer',
    image: 'https://images.unsplash.com/photo-1633625510483-c177f4308f33?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIwaGVhZHNob3RzfGVufDB8fHx8MTc3NjM1NDgzNnww&ixlib=rb-4.1.0&q=85',
    rating: 5,
    text: 'As a freelancer, finding the right projects was always challenging. Kaamhub\'s AI matching connected me with perfect clients, and we\'ve completed multiple successful projects!',
    gradient: 'from-purple-500 to-pink-400'
  },
  {
    name: 'Emily Rodriguez',
    role: 'Content Writer & Marketing Specialist',
    image: 'https://images.unsplash.com/photo-1571756978657-e7db0b92f7d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxkaXZlcnNlJTIwaGVhZHNob3RzfGVufDB8fHx8MTc3NjM1NDgzNnww&ixlib=rb-4.1.0&q=85',
    rating: 5,
    text: 'I love the community features! Networking with other freelancers and sharing insights has been amazing. Plus, the payment system is super secure and hassle-free.',
    gradient: 'from-amber-500 to-orange-400'
  }
];

const Testimonials = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll('.testimonial-card');
            cards.forEach((card, index) => {
              setTimeout(() => {
                card.classList.add('animate-fade-in-up');
              }, index * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="testimonials"
      className="py-28 md:py-24 relative overflow-hidden"
      ref={sectionRef}
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-y-16 left-0 h-64 w-64 rounded-full bg-blue-500/10 blur-[120px]"></div>
        <div className="absolute right-0 top-16 h-64 w-64 rounded-full bg-cyan-500/10 blur-[120px]"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <SectionHeader
          eyebrow="Testimonials"
          title="Loved by Freelancers"
          accent="& Clients Worldwide"
          description="Don't just take our word for it. Here's what our community has to say about Kaamhub."
          className="mb-14 md:mb-16"
        />

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <Card
              key={index}
              className="testimonial-card opacity-0 group hover:shadow-2xl transition-all duration-500 bg-white/85 dark:bg-slate-900/75 backdrop-blur-sm border border-slate-200/80 dark:border-white/10 hover:border-blue-200 dark:hover:border-cyan-800 overflow-hidden relative"
            >
              {/* Quote Icon Background */}
              <div className={`absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br ${testimonial.gradient} opacity-10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500`}></div>

              <CardContent className="p-8 relative z-10">
                {/* Quote Icon */}
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${testimonial.gradient} flex items-center justify-center mb-6 shadow-lg`}>
                  <Quote className="w-6 h-6 text-white" />
                </div>

                {/* Rating */}
                <div className="flex space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 fill-amber-400 text-amber-400"
                    />
                  ))}
                </div>

                {/* Testimonial Text */}
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6 italic">
                  "{testimonial.text}"
                </p>

                {/* Author */}
                <div className="flex items-center space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-14 h-14 rounded-full object-cover ring-4 ring-gray-100 dark:ring-gray-700 transform group-hover:scale-110 transition-transform duration-300"
                  />
                  <div>
                    <div className="font-bold text-gray-900 dark:text-white text-lg">
                      {testimonial.name}
                    </div>
                    <div className={`text-sm text-transparent bg-clip-text bg-gradient-to-r ${testimonial.gradient} font-medium`}>
                      {testimonial.role}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Trust Badge */}
        <div className="mt-16 flex justify-center lg:mt-20">
          <div className="inline-flex items-center space-x-3 rounded-full border border-slate-200/80 bg-white/85 px-8 py-4 shadow-lg backdrop-blur-sm dark:border-white/10 dark:bg-slate-900/80">
            <Star className="w-6 h-6 fill-cyan-500 text-cyan-500" />
            <span className="text-xl font-bold text-gray-900 dark:text-white">4.8/5 Average Rating</span>
            <span className="text-gray-600 dark:text-gray-400">from 10,000+ reviews</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
